import { MandateState, Source, Status } from '@smg-real-estate/seller-leads-schema';
import { type ValidationRuleWithParams } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import { INITIAL_PAGE } from '@/api/sellerLeads';
import {
  LeadSource,
  LeadSourceToDisplayableSource,
  QualifiedOptions,
  Sort,
} from '@/models/sellerLead';
import type { Filters } from '@/store/sellerLeads/SellerLeadsStore';

interface FiltersObjectForUrl {
  source?: Source[];
  status?: Status[];
  searchText?: string;
  sortOrder?: Sort;
  qualified?: QualifiedOptions[];
  mandateState?: MandateState[];
}

export const getQueryParams = (): Filters => {
  const queryParams = new URLSearchParams(window.location.search);
  const filters = queryParams.get('filters');

  let filtersObj: FiltersObjectForUrl = {};
  if (filters) {
    try {
      filtersObj = JSON.parse(decodeURIComponent(filters));
    } catch {
      console.warn(`Filters is an invalid JSON object from URL, filters: ${filters}`);
    }
  }
  const {
    source,
    status,
    searchText = '',
    sortOrder = Sort.Desc,
    qualified,
    mandateState,
  } = filtersObj;

  return {
    searchText,
    status: status ?? Object.values(Status),
    source: source
      ? Array.from(new Set(source.map((item: Source) => LeadSourceToDisplayableSource[item])))
      : Object.values(LeadSource),
    sortOrder,
    page: parseInt(queryParams.get('page') || INITIAL_PAGE.toString(), 10),
    qualified: qualified ?? Object.values(QualifiedOptions),
    mandateState: mandateState ?? Object.values(MandateState),
  };
};

export const joinNonEmpty = (items: (string | undefined | null)[], separator = ' '): string => {
  return items.filter((item) => !!item).join(separator);
};

export const trimmedMinLength = (minLength: number): ValidationRuleWithParams =>
  helpers.withParams({ minLength }, (value: string) => {
    const trimmedValue = value.trim();
    return trimmedValue.length === 0 || trimmedValue.length >= minLength;
  });

export const isTwoWorkDaysLate = (
  purchasedDateWithTime: Date,
  currentTime: Date = new Date(),
): boolean => {
  if (currentTime.getTime() <= purchasedDateWithTime.getTime()) {
    return false;
  }

  let workDaysPassed = 0;
  const dayIterator = new Date(purchasedDateWithTime.getTime());

  while (dayIterator.getTime() < currentTime.getTime()) {
    if (isWorkDay(dayIterator)) {
      workDaysPassed++;
    }
    dayIterator.setDate(dayIterator.getDate() + 1);
  }

  return workDaysPassed > 2;
};

const isWorkDay = (date: Date): boolean => {
  const dayOfWeek = date.getDay();
  return dayOfWeek !== 0 && dayOfWeek !== 6;
};
